/**
 * Presentational primitives shared by the Cibles panel, its list views and its dialogs, so
 * every pill, dialog header/footer and field label reads the same. Styling only: no state,
 * no data access. Tokens come from the Brand Knowledge list components (`K`, `microLabel`).
 */
import type { ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { K, microLabel } from '../../components/brand/knowledge/tokens';

export { K, microLabel };

// ---------------------------------------------------------------------------
// Pill
// ---------------------------------------------------------------------------

export type PillTone = 'neutral' | 'tag' | 'accent' | 'warn' | 'ok' | 'info';

const TONES: Record<Exclude<PillTone, 'accent'>, { bg: string; fg: string }> = {
  neutral: { bg: K.chipBg, fg: K.ink2 },
  tag: { bg: K.chipBg, fg: '#3F3F46' },
  warn: { bg: 'rgba(245,158,11,.14)', fg: K.warningInk },
  ok: { bg: 'rgba(16,185,129,.12)', fg: '#047857' },
  info: { bg: 'rgba(14,165,233,.12)', fg: '#0369A1' },
};

/** The mock's `.chip`: one height, one radius, one type size for every small label. */
export function Pill({
  children, tone = 'neutral', hex, onClick, maxWidth, sx,
}: {
  children: ReactNode;
  tone?: PillTone;
  /** Only for `accent`: the hue to tint with. */
  hex?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  maxWidth?: number | string;
  sx?: SxProps<Theme>;
}) {
  const c = tone === 'accent'
    ? { bg: alpha(hex ?? K.primary, 0.1), fg: hex ?? K.primaryDark }
    : TONES[tone];
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={[
        {
          display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
          maxWidth: maxWidth ?? 'none', minWidth: 0,
          height: 20, px: '7px', borderRadius: '6px',
          fontSize: 11.5, fontWeight: tone === 'tag' ? 500 : 600, lineHeight: '20px',
          bgcolor: c.bg, color: c.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          cursor: onClick ? 'pointer' : 'inherit',
          ...(onClick ? { '&:hover': { filter: 'brightness(0.96)' } } : {}),
          ...(tone === 'tag' ? { '&::before': { content: '"#"', opacity: 0.45 } } : {}),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
}

/** Row-level warning marker; the messages live in the tooltip, as before. */
export function WarnPill({ warnings }: { warnings?: { kind: string; message: string }[] }) {
  if (!warnings?.length) return null;
  return (
    <Tooltip title={warnings.map((w) => w.message || w.kind).join(' · ')}>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <Pill tone="warn" sx={{ px: '5px', cursor: 'help' }}>
          <WarningAmberIcon sx={{ fontSize: 13 }} />
        </Pill>
      </Box>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Row action icon buttons
// ---------------------------------------------------------------------------

/** Quiet by default, coloured on hover; `danger` for delete. */
export const rowActionSx = (danger = false) => ({
  p: '4px', borderRadius: '7px', color: K.ink3, flexShrink: 0,
  '&:hover': danger
    ? { color: K.error, bgcolor: 'rgba(239,68,68,.08)' }
    : { color: K.ink, bgcolor: K.segBg },
}) as const;

// ---------------------------------------------------------------------------
// Dialog chrome
// ---------------------------------------------------------------------------

export const dialogPaperProps = {
  sx: { borderRadius: '14px', border: `1px solid ${K.line}`, boxShadow: '0 24px 48px rgba(15,15,30,.14)' },
};

export function DialogHead({
  title, subtitle, onClose, disabled,
}: { title: ReactNode; subtitle?: ReactNode; onClose: () => void; disabled?: boolean }) {
  return (
    <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5, borderBottom: `1px solid ${K.line}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: K.tight, fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: K.ink }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ mt: 0.375, fontSize: 12.5, color: K.ink2, lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={onClose} disabled={disabled} sx={{ mt: -0.25, mr: -0.75, ...rowActionSx() }}>
        <CloseIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Box>
  );
}

export function DialogFoot({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ px: 3, py: 1.75, borderTop: `1px solid ${K.line}`, bgcolor: K.headBg, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
      {children}
    </Box>
  );
}

/** Body of every Cibles dialog: one vertical rhythm. */
export const dialogContentSx = {
  pt: '20px !important', pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2.25,
} as const;

/** Field label of the dialogs (optional marker in grey). */
export function FieldLabel({ children, optional, hint }: { children: ReactNode; optional?: boolean; hint?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography component="div" sx={{ fontSize: 12.5, fontWeight: 600, color: K.ink }}>
        {children}
        {optional && (
          <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: K.ink3 }}>
            {t('cibles.col_optional')}
          </Box>
        )}
      </Typography>
      {hint && <Typography sx={{ mt: 0.25, fontSize: 11.5, color: K.ink3, lineHeight: 1.45 }}>{hint}</Typography>}
    </Box>
  );
}

/** Small helper line under a field. */
export function FieldHint({ children }: { children: ReactNode }) {
  return <Typography sx={{ mt: 0.5, fontSize: 11.5, color: K.ink3, lineHeight: 1.45 }}>{children}</Typography>;
}

/** Primary / secondary dialog buttons share these. */
export const primaryBtnSx = {
  fontSize: 13, fontWeight: 600, borderRadius: '10px', px: 2, boxShadow: 'none',
  bgcolor: K.primary, '&:hover': { bgcolor: K.primaryDark, boxShadow: 'none' },
} as const;
export const secondaryBtnSx = {
  fontSize: 13, fontWeight: 600, borderRadius: '10px', px: 1.75, color: K.ink,
  borderColor: K.line, bgcolor: K.paper, '&:hover': { borderColor: K.line, bgcolor: K.primarySoft },
} as const;

/** Inputs inside dialogs: the mock's `.input` (quiet border, focus ring). */
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: K.paper,
    '& fieldset': { borderColor: K.line },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,.18)' },
    '&.Mui-focused fieldset': { borderColor: K.primary, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: K.focusRing },
  },
  '& .MuiInputBase-input': { fontSize: 13 },
} as const;

/** A `<Select>` in a dialog — same border and focus as `inputSx`. */
export const selectSx = {
  borderRadius: '10px', fontSize: 13, bgcolor: K.paper,
  '& fieldset': { borderColor: K.line },
  '&:hover fieldset': { borderColor: 'rgba(0,0,0,.18) !important' },
  '&.Mui-focused fieldset': { borderColor: `${K.primary} !important`, borderWidth: '1px !important' },
} as const;

/** Floating dropdown panel (typeaheads, suggestion lists). */
export const dropdownSx = {
  bgcolor: K.paper, border: `1px solid ${K.line}`, borderRadius: '10px',
  boxShadow: '0 12px 28px rgba(15,15,30,.12)', overflow: 'hidden',
} as const;
